/**
 * ReferenceDataStore — shared Zustand cache for low-change reference data.
 * Phase 0.5: users lookup/assignable + pipelines only.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ApiClient } from '../services/ApiClient.js';
import { mapUserFromApi } from '../services/crm/ApiEntityMapper.js';

const DEFAULT_POLICIES = {
    users_ttl_sec: 1800,
    pipelines_ttl_sec: 3600,
    deals_list_ttl_sec: 30,
    dashboard_ttl_sec: 45,
};

function emptySlice() {
    return {
        data: null,
        etag: null,
        version: null,
        fetchedAt: 0,
        expiresAt: 0,
        status: 'idle',
        error: null,
    };
}

function isFresh(slice, now = Date.now()) {
    return Array.isArray(slice?.data) && slice.expiresAt > now;
}

function isUsable(slice) {
    return Array.isArray(slice?.data);
}

const inflight = {
    usersLookup: null,
    usersAssignable: new Map(),
    pipelines: null,
    syncState: null,
};

function assignableKey(includeIds = []) {
    return [...includeIds].filter(Boolean).map(String).sort().join(',') || '_default';
}

export const useReferenceDataStore = create(
    persist(
        (set, get) => ({
            usersLookup: emptySlice(),
            usersAssignable: emptySlice(),
            pipelines: emptySlice(),
            policies: { ...DEFAULT_POLICIES },
            syncVersions: { users: null, pipelines: null },

            setPolicies(policies) {
                if (!policies || typeof policies !== 'object') return;
                set({ policies: { ...get().policies, ...policies } });
            },

            clearReferenceData() {
                set({
                    usersLookup: emptySlice(),
                    usersAssignable: emptySlice(),
                    pipelines: emptySlice(),
                    syncVersions: { users: null, pipelines: null },
                });
                inflight.usersLookup = null;
                inflight.usersAssignable.clear();
                inflight.pipelines = null;
            },

            async ensureUsersLookup({ force = false } = {}) {
                const state = get();
                const slice = state.usersLookup;
                const now = Date.now();

                if (!force && isFresh(slice, now)) return slice.data;
                if (!force && isUsable(slice) && slice.status !== 'loading') {
                    // SWR: return cache immediately, refresh in background
                    void get()._fetchUsersLookup({ background: true });
                    return slice.data;
                }
                return get()._fetchUsersLookup({ force });
            },

            async _fetchUsersLookup({ force = false, background = false } = {}) {
                if (inflight.usersLookup) return inflight.usersLookup;

                const prev = get().usersLookup;
                if (!background) {
                    set({
                        usersLookup: {
                            ...prev,
                            status: prev.data ? 'revalidating' : 'loading',
                            error: null,
                        },
                    });
                }

                const ttlMs = (get().policies.users_ttl_sec || DEFAULT_POLICIES.users_ttl_sec) * 1000;
                const promise = (async () => {
                    try {
                        const res = await ApiClient.getWithMeta('/api/v1/users/lookup', {
                            etag: force ? null : prev.etag,
                        });
                        if (res.notModified && isUsable(prev)) {
                            const now = Date.now();
                            const next = {
                                ...prev,
                                etag: res.etag || prev.etag,
                                fetchedAt: now,
                                expiresAt: now + ttlMs,
                                status: 'fresh',
                                error: null,
                            };
                            set({ usersLookup: next });
                            return next.data;
                        }
                        const rows = Array.isArray(res.data) ? res.data : [];
                        const mapped = rows.map(mapUserFromApi);
                        const now = Date.now();
                        set({
                            usersLookup: {
                                data: mapped,
                                etag: res.etag || null,
                                version: get().syncVersions.users,
                                fetchedAt: now,
                                expiresAt: now + ttlMs,
                                status: 'fresh',
                                error: null,
                            },
                        });
                        return mapped;
                    } catch (err) {
                        // stale-if-error
                        if (isUsable(prev)) {
                            set({
                                usersLookup: {
                                    ...prev,
                                    status: 'stale',
                                    error: err?.message || 'Failed to refresh users',
                                },
                            });
                            return prev.data;
                        }
                        set({
                            usersLookup: {
                                ...emptySlice(),
                                status: 'error',
                                error: err?.message || 'Failed to load users',
                            },
                        });
                        throw err;
                    } finally {
                        inflight.usersLookup = null;
                    }
                })();

                inflight.usersLookup = promise;
                return promise;
            },

            async ensureUsersAssignable({ force = false, includeUserIds = [] } = {}) {
                const key = assignableKey(includeUserIds);
                // Default assignable (no include ids) uses shared slice; custom include ids are ephemeral
                if (key !== '_default') {
                    return get()._fetchUsersAssignable({ force, includeUserIds, cacheKey: key });
                }

                const state = get();
                const slice = state.usersAssignable;
                const now = Date.now();
                if (!force && isFresh(slice, now)) return slice.data;
                if (!force && isUsable(slice) && slice.status !== 'loading') {
                    void get()._fetchUsersAssignable({ background: true, includeUserIds: [], cacheKey: key });
                    return slice.data;
                }
                return get()._fetchUsersAssignable({ force, includeUserIds: [], cacheKey: key });
            },

            async _fetchUsersAssignable({ force = false, background = false, includeUserIds = [], cacheKey = '_default' } = {}) {
                if (inflight.usersAssignable.has(cacheKey)) {
                    return inflight.usersAssignable.get(cacheKey);
                }

                const prev = cacheKey === '_default' ? get().usersAssignable : emptySlice();
                if (cacheKey === '_default' && !background) {
                    set({
                        usersAssignable: {
                            ...prev,
                            status: prev.data ? 'revalidating' : 'loading',
                            error: null,
                        },
                    });
                }

                const ttlMs = (get().policies.users_ttl_sec || DEFAULT_POLICIES.users_ttl_sec) * 1000;
                const params = {};
                if (includeUserIds?.length) {
                    params.include_ids = includeUserIds.filter(Boolean).join(',');
                }

                const promise = (async () => {
                    try {
                        const res = await ApiClient.getWithMeta('/api/v1/users/assignable', {
                            params,
                            etag: force || cacheKey !== '_default' ? null : prev.etag,
                        });
                        if (res.notModified && isUsable(prev) && cacheKey === '_default') {
                            const now = Date.now();
                            const next = {
                                ...prev,
                                etag: res.etag || prev.etag,
                                fetchedAt: now,
                                expiresAt: now + ttlMs,
                                status: 'fresh',
                                error: null,
                            };
                            set({ usersAssignable: next });
                            return next.data;
                        }
                        const rows = Array.isArray(res.data) ? res.data : [];
                        const mapped = rows.map(mapUserFromApi);
                        if (cacheKey === '_default') {
                            const now = Date.now();
                            set({
                                usersAssignable: {
                                    data: mapped,
                                    etag: res.etag || null,
                                    version: get().syncVersions.users,
                                    fetchedAt: now,
                                    expiresAt: now + (mapped.length === 0 ? Math.min(ttlMs, 60_000) : ttlMs),
                                    status: 'fresh',
                                    error: null,
                                },
                            });
                        }
                        return mapped;
                    } catch (err) {
                        if (cacheKey === '_default' && isUsable(prev)) {
                            set({
                                usersAssignable: {
                                    ...prev,
                                    status: 'stale',
                                    error: err?.message || 'Failed to refresh assignable users',
                                },
                            });
                            return prev.data;
                        }
                        if (cacheKey === '_default') {
                            set({
                                usersAssignable: {
                                    ...emptySlice(),
                                    status: 'error',
                                    error: err?.message || 'Failed to load assignable users',
                                },
                            });
                        }
                        throw err;
                    } finally {
                        inflight.usersAssignable.delete(cacheKey);
                    }
                })();

                inflight.usersAssignable.set(cacheKey, promise);
                return promise;
            },

            async ensurePipelines({ force = false } = {}) {
                const state = get();
                const slice = state.pipelines;
                const now = Date.now();
                if (!force && isFresh(slice, now)) return slice.data;
                if (!force && isUsable(slice) && slice.status !== 'loading') {
                    void get()._fetchPipelines({ background: true });
                    return slice.data;
                }
                return get()._fetchPipelines({ force });
            },

            async _fetchPipelines({ force = false, background = false } = {}) {
                if (inflight.pipelines) return inflight.pipelines;

                const prev = get().pipelines;
                if (!background) {
                    set({
                        pipelines: {
                            ...prev,
                            status: prev.data ? 'revalidating' : 'loading',
                            error: null,
                        },
                    });
                }

                const ttlMs = (get().policies.pipelines_ttl_sec || DEFAULT_POLICIES.pipelines_ttl_sec) * 1000;
                const promise = (async () => {
                    try {
                        const res = await ApiClient.getWithMeta('/api/v1/pipelines', {
                            etag: force ? null : prev.etag,
                        });
                        if (res.notModified && isUsable(prev)) {
                            const now = Date.now();
                            const next = {
                                ...prev,
                                etag: res.etag || prev.etag,
                                fetchedAt: now,
                                expiresAt: now + ttlMs,
                                status: 'fresh',
                                error: null,
                            };
                            set({ pipelines: next });
                            return next.data;
                        }
                        const raw = res.data;
                        const list = Array.isArray(raw) ? raw : (raw?.data || raw?.items || []);
                        const now = Date.now();
                        set({
                            pipelines: {
                                data: list,
                                etag: res.etag || null,
                                version: get().syncVersions.pipelines,
                                fetchedAt: now,
                                expiresAt: now + ttlMs,
                                status: 'fresh',
                                error: null,
                            },
                        });
                        return list;
                    } catch (err) {
                        if (isUsable(prev)) {
                            set({
                                pipelines: {
                                    ...prev,
                                    status: 'stale',
                                    error: err?.message || 'Failed to refresh pipelines',
                                },
                            });
                            return prev.data;
                        }
                        // Fail closed for missing pipeline:view — empty list
                        const now = Date.now();
                        set({
                            pipelines: {
                                data: [],
                                etag: null,
                                version: null,
                                fetchedAt: now,
                                expiresAt: now + Math.min(ttlMs, 60_000),
                                status: 'error',
                                error: err?.message || 'Failed to load pipelines',
                            },
                        });
                        return [];
                    } finally {
                        inflight.pipelines = null;
                    }
                })();

                inflight.pipelines = promise;
                return promise;
            },

            async pullSyncState() {
                if (inflight.syncState) return inflight.syncState;
                inflight.syncState = (async () => {
                    try {
                        const data = await ApiClient.get('/api/v1/app/sync-state');
                        if (data?.policies) get().setPolicies(data.policies);
                        set({
                            syncVersions: {
                                users: data?.users ?? null,
                                pipelines: data?.pipelines ?? null,
                            },
                        });
                        return data;
                    } finally {
                        inflight.syncState = null;
                    }
                })();
                return inflight.syncState;
            },

            async revalidateFromSyncState() {
                const data = await get().pullSyncState();
                const state = get();
                const tasks = [];
                if (data?.users && state.usersLookup.version && data.users !== state.usersLookup.version) {
                    tasks.push(get()._fetchUsersLookup({ force: true }));
                    tasks.push(get()._fetchUsersAssignable({ force: true, includeUserIds: [], cacheKey: '_default' }));
                } else if (data?.users && !state.usersLookup.version && isUsable(state.usersLookup)) {
                    set({
                        usersLookup: { ...state.usersLookup, version: data.users },
                        usersAssignable: isUsable(state.usersAssignable)
                            ? { ...state.usersAssignable, version: data.users }
                            : state.usersAssignable,
                    });
                }
                if (data?.pipelines && state.pipelines.version && data.pipelines !== state.pipelines.version) {
                    tasks.push(get()._fetchPipelines({ force: true }));
                } else if (data?.pipelines && !state.pipelines.version && isUsable(state.pipelines)) {
                    set({ pipelines: { ...state.pipelines, version: data.pipelines } });
                }
                if (tasks.length) await Promise.allSettled(tasks);
                // Stamp versions after refresh
                const after = get();
                if (data?.users) {
                    if (isUsable(after.usersLookup)) {
                        set({ usersLookup: { ...get().usersLookup, version: data.users } });
                    }
                    if (isUsable(after.usersAssignable)) {
                        set({ usersAssignable: { ...get().usersAssignable, version: data.users } });
                    }
                }
                if (data?.pipelines && isUsable(get().pipelines)) {
                    set({ pipelines: { ...get().pipelines, version: data.pipelines } });
                }
                return data;
            },
        }),
        {
            name: 'crm-reference-data-v1',
            partialize: (state) => ({
                // Persist non-sensitive lookup + pipeline labels only
                usersLookup: {
                    data: state.usersLookup.data,
                    etag: state.usersLookup.etag,
                    version: state.usersLookup.version,
                    fetchedAt: state.usersLookup.fetchedAt,
                    expiresAt: state.usersLookup.expiresAt,
                    status: 'stale',
                    error: null,
                },
                pipelines: {
                    data: state.pipelines.data,
                    etag: state.pipelines.etag,
                    version: state.pipelines.version,
                    fetchedAt: state.pipelines.fetchedAt,
                    expiresAt: state.pipelines.expiresAt,
                    status: 'stale',
                    error: null,
                },
                policies: state.policies,
                syncVersions: state.syncVersions,
            }),
        }
    )
);

/** Non-hook accessors for vanilla services */
export const ReferenceDataStore = {
    getState: () => useReferenceDataStore.getState(),
    ensureUsersLookup: (opts) => useReferenceDataStore.getState().ensureUsersLookup(opts),
    ensureUsersAssignable: (opts) => useReferenceDataStore.getState().ensureUsersAssignable(opts),
    ensurePipelines: (opts) => useReferenceDataStore.getState().ensurePipelines(opts),
    clear: () => useReferenceDataStore.getState().clearReferenceData(),
    revalidateFromSyncState: () => useReferenceDataStore.getState().revalidateFromSyncState(),
    pullSyncState: () => useReferenceDataStore.getState().pullSyncState(),
};
